import React, { useEffect, useState } from "react";
import { Sparkles, Loader2, RefreshCcw, Lightbulb, Lock, ChevronDown, ChevronUp } from "lucide-react";
import { endpoints } from "../lib/api";
import { isAIEnabled, planLockReason } from "../lib/plan";

export default function AIInsights() {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(isAIEnabled());
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const aiEnabled = isAIEnabled();

  const load = async () => {
    if (!aiEnabled) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await endpoints.aiInsights();
      setInsights(data?.insights || []);
    } catch (err) {
      setError(err?.response?.data?.detail || "AI insights unavailable");
      setInsights([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <section className="mb-8" data-testid="ai-insights-section">
      <div
        className="rounded-2xl p-5 md:p-6 relative overflow-hidden border border-purple-200/40 dark:border-purple-800/30"
        style={{
          background:
            "linear-gradient(135deg, rgba(124,58,237,0.08) 0%, rgba(219,39,119,0.06) 50%, rgba(249,115,22,0.06) 100%)",
        }}
      >
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div className="flex items-center gap-2.5">
            <span
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-white shrink-0"
              style={{ background: "linear-gradient(135deg, #7C3AED 0%, #DB2777 50%, #F97316 100%)" }}
              aria-hidden
            >
              <Sparkles className="w-4 h-4" />
            </span>
            <div>
              <h2 className="font-display text-lg font-bold text-foreground leading-none flex items-center gap-2">
                AI Insights
                <span className="text-[9px] font-bold uppercase tracking-wider bg-foreground text-background px-1.5 py-0.5 rounded">
                  beta
                </span>
              </h2>
              <p className="text-[11px] text-muted-foreground mt-1">Smart helper · refreshed each visit</p>
            </div>
          </div>
          <button
            type="button"
            onClick={load}
            disabled={loading || !aiEnabled}
            data-testid="ai-insights-refresh"
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5" />}
            Refresh
          </button>
        </div>

        {!aiEnabled ? (
          <div data-testid="ai-insights-locked" className="rounded-lg border border-dashed border-purple-300/60 dark:border-purple-700/40 p-4 text-center">
            <Lock className="w-5 h-5 mx-auto mb-2 text-purple-500" />
            <p className="text-sm font-medium text-foreground mb-1">AI insights</p>
            <p className="text-xs text-muted-foreground max-w-md mx-auto">{planLockReason()}</p>
          </div>
        ) : loading ? (
          <div className="space-y-2" data-testid="ai-insights-loading">
            <div className="h-4 bg-muted/50 rounded animate-pulse w-3/4" />
            <div className="h-4 bg-muted/50 rounded animate-pulse w-2/3" />
            <div className="h-4 bg-muted/50 rounded animate-pulse w-4/5" />
          </div>
        ) : error ? (
          <div className="text-sm text-muted-foreground/80 italic" data-testid="ai-insights-error">
            {error}. <button onClick={load} className="text-foreground hover:underline">Try again</button>.
          </div>
        ) : insights.length === 0 ? (
          <div className="text-sm text-muted-foreground/70 italic">No insights right now - check back after some activity.</div>
        ) : (
          <>
            <ul className="space-y-2" data-testid="ai-insights-list">
              {(expanded ? insights : insights.slice(0, 3)).map((line, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-foreground/90" data-testid={`ai-insight-${i}`}>
                  <Lightbulb className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
            {insights.length > 3 && (
              <button
                type="button"
                onClick={() => setExpanded(v => !v)}
                data-testid="ai-insights-view-all"
                className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-foreground/80 hover:text-foreground transition-colors"
              >
                {expanded ? (
                  <>Show less <ChevronUp className="w-3.5 h-3.5" /></>
                ) : (
                  <>View all {insights.length} insights <ChevronDown className="w-3.5 h-3.5" /></>
                )}
              </button>
            )}
          </>
        )}
      </div>
    </section>
  );
}
